import { useState } from 'react';
import type { DailyCalendarEvent } from '../../../lib/dailyApi';
import { useI18n } from '../../../contexts/I18nContext';

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
}

// Minutes from midnight for an event timestamp (local time).
function minutesOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

function durationMinutes(event: DailyCalendarEvent): number {
  return Math.max(0, minutesOfDay(event.endTime) - minutesOfDay(event.startTime));
}

// An event spanning most of the working day reads as a "full-day" band even if
// it is not flagged allDay (e.g. a conference 09:00–23:30).
const LONG_EVENT_MINUTES = 6 * 60;

function isLongEvent(event: DailyCalendarEvent): boolean {
  return event.allDay || durationMinutes(event) >= LONG_EVENT_MINUTES;
}

// Descriptions are already stored as plain text (HTML stripped server-side).
const DESCRIPTION_MAX = 200;

function truncate(text: string, max = DESCRIPTION_MAX): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  const slice = trimmed.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > max * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trimEnd()}\u2026`;
}

// --- Color coding per event type (CLE-269) ---------------------------------
// Fallback palette, used when the event carries no colour of its own. The
// category is inferred from the calendar name so existing simulated events keep
// working. Each style bundles the accent bar, chip and dot colors.
type EventStyle = {
  labelKey: string;
  bar: string;
  chipBg: string;
  chipText: string;
  dot: string;
};

const STYLES: Record<string, EventStyle> = {
  conference: {
    labelKey: 'daily.event.conference',
    bar: 'bg-purple-500',
    chipBg: 'bg-purple-100',
    chipText: 'text-purple-700',
    dot: 'bg-purple-500',
  },
  speech: {
    labelKey: 'daily.event.speech',
    bar: 'bg-emerald-500',
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-700',
    dot: 'bg-emerald-500',
  },
  team: {
    labelKey: 'daily.event.team',
    bar: 'bg-slate-400',
    chipBg: 'bg-slate-100',
    chipText: 'text-slate-700',
    dot: 'bg-slate-400',
  },
  admin: {
    labelKey: 'daily.event.admin',
    bar: 'bg-amber-400',
    chipBg: 'bg-amber-100',
    chipText: 'text-amber-800',
    dot: 'bg-amber-400',
  },
  personal: {
    labelKey: 'daily.event.personal',
    bar: 'bg-blue-400',
    chipBg: 'bg-blue-100',
    chipText: 'text-blue-700',
    dot: 'bg-blue-400',
  },
  client: {
    labelKey: 'daily.event.client',
    bar: 'bg-indigo-500',
    chipBg: 'bg-indigo-100',
    chipText: 'text-indigo-700',
    dot: 'bg-indigo-500',
  },
};

function styleFor(event: DailyCalendarEvent): EventStyle {
  const name = event.calendarName.toLowerCase();
  const summary = event.summary.toLowerCase();
  if (summary.includes('speech') || summary.includes('prezentare') || name === 'speech')
    return STYLES.speech;
  if (name === 'conferință' || name === 'conferinta' || summary.includes('conferin'))
    return STYLES.conference;
  if (name === 'admin' || summary.includes('check') || summary.includes('retro'))
    return STYLES.admin;
  if (name === 'echipă' || name === 'echipa' || name === 'team' || summary.includes('team'))
    return STYLES.team;
  if (name === 'clienți' || name === 'clienti') return STYLES.client;
  if (name === 'personal') return STYLES.personal;
  return STYLES.team;
}

interface Props {
  events: DailyCalendarEvent[];
}

/** A single timeline row; click to expand the description/details. */
function EventRow({ event }: { event: DailyCalendarEvent }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const style = styleFor(event);
  const long = isLongEvent(event);
  const hasDetails = Boolean(event.description?.trim() || event.location);

  return (
    <li className="flex items-stretch gap-3">
      {/* Accent bar — full-height band for long/all-day events. When the event
          carries a colour the user picked in Google Calendar, that wins: it is
          the colour code they already read their own day by. The inferred
          category palette only fills in for events without one. */}
      <span
        aria-hidden
        className={`w-1.5 shrink-0 rounded-full ${event.color ? '' : style.bar} ${
          long ? 'opacity-100' : 'opacity-80'
        }`}
        style={event.color ? { backgroundColor: event.color } : undefined}
      />
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => hasDetails && setOpen((v) => !v)}
          className={`flex w-full items-start justify-between gap-2 text-left ${
            hasDetails ? 'cursor-pointer' : 'cursor-default'
          }`}
          aria-expanded={hasDetails ? open : undefined}
        >
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-900">{event.summary}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${style.chipBg} ${style.chipText}`}
              >
                {t(style.labelKey)}
              </span>
              {long && (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                  {t('daily.calendar.allDay')}
                </span>
              )}
            </span>
            <span className="mt-0.5 block text-xs text-gray-500">
              {event.allDay
                ? t('daily.calendar.allDay')
                : `${formatTime(event.startTime)} – ${formatTime(event.endTime)}`}
              {event.location ? ` · ${event.location}` : ''}
            </span>
          </span>
          {hasDetails && (
            <span aria-hidden className="mt-0.5 shrink-0 text-xs text-gray-400">
              {open ? '▲' : '▼'}
            </span>
          )}
        </button>
        {open && event.description?.trim() && (
          <p className="mt-1 whitespace-pre-line rounded bg-gray-50 p-2 text-xs text-gray-600">
            {truncate(event.description)}
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * Calendar events for the daily summary, rendered as a chronological timeline
 * with color-coded time blocks. An event that was colour-coded in Google
 * Calendar keeps that exact colour here, so the day reads the same in both
 * places; the rest fall back to a colour inferred from the event type
 * (CLE-269). Long / all-day events
 * (e.g. a conference) render as a full-height band; short events as distinct
 * blocks. Click an event to expand its details.
 */
export function CalendarEventsSection({ events }: Props) {
  const { t } = useI18n();
  const sorted = [...events].sort((a, b) => minutesOfDay(a.startTime) - minutesOfDay(b.startTime));

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="mb-3 font-semibold text-gray-800">{t('daily.calendar')}</h2>
      {sorted.length === 0 ? (
        <p className="text-sm text-gray-500">{t('daily.calendar.empty')}</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {sorted.map((event) => (
            <EventRow key={event.id} event={event} />
          ))}
        </ul>
      )}
    </section>
  );
}
